import { Filter, Search, X } from 'lucide-react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import context from '../../context/context';
import LoadingAnimation from '../loading-animation/loading-animation';
import './news-and-media-filters.scss';

const className = 'news-and-media-filters';

const NewsAndMediaFilters = ({ data }) => {
	const {
		setTriggerRefresh,
		setFiltersGlobal,
		setContentGlobal,
		setAdditionalGlobal,
	} = useContext(context);
	const [searchTerm, setSearchTerm] = useState('');
	const mediaType_InputRef = useRef();
	const disciplines_InputRef = useRef();
	const cat_InputRef = useRef();

	const [filters, setFilters] = useState({
		portfolioList: [],
		portfolios: [],
		disciplineList: [],
		disciplines: [],
		mediaList: [],
		media: [],
		catList: [],
		cats: [],
	});

	const customStyles = {
		control: (provided, state) => ({
			...provided,
			boxShadow: 'var(--box-shadow-1)',
			border: '1px solid transparent',
			borderRadius: 'var(--border-radius)',
			backgroundColor: 'var(--c-white)',
			minHeight: '60px',
			'&:hover': {
				border: '1px solid var(--c-teal)',
			},
			...(state.isFocused && {
				border: '1px solid var(--c-teal)',
			}),
		}),
		dropdownIndicator: (provided) => ({
			...provided,
			color: 'var(--c-teal)',
		}),
		multiValue: (provided) => ({
			...provided,
			backgroundColor: 'var(--c-teal)',
			borderRadius: 'var(--border-radius)',
			padding: '0.75rem',
		}),
		multiValueLabel: (provided) => ({
			...provided,
			color: 'white',
		}),
		multiValueRemove: (provided) => ({
			...provided,
			color: 'white',
			':hover': {
				backgroundColor: 'darkred',
				color: 'white',
			},
		}),
	};

	useEffect(() => {
		if (data.success) {
			setFilters((prev) => ({
				...prev,
				disciplineList: data.data.discipline || [],
				mediaList: data.data.media_type || [],
				catList: data.data.category || [],
				portfolioList: data.data.portfolio || [],
			}));
		}
	}, [data]);

	const handleReset = () => {
		setSearchTerm('');
		mediaType_InputRef.current.clearValue();
		disciplines_InputRef.current.clearValue();
		cat_InputRef.current.clearValue();

		setFilters((prevFilters) => ({
			...prevFilters,
			portfolios: [],
			disciplines: [],
			media: [],
			cats: [],
		}));

		setFiltersGlobal({
			search_term: '',
			discipline: [],
			media_type: [],
			category: [],
			portfolio: [],
		});

		setAdditionalGlobal({
			page: 1,
		});

		setContentGlobal([]);
		setTriggerRefresh(true);
	};

	const handleInputChange = useCallback(
		(setter) => (selectedOptions) => {
			const values = selectedOptions
				? selectedOptions.map((option) => option.value)
				: [];
			setFilters((prev) => ({
				...prev,
				[setter]: values,
			}));
		},
		[]
	);

	const handlePortfolioChange = (term_id) => {
		setFilters((prev) => {
			const portfolios = prev.portfolios.includes(term_id)
				? prev.portfolios.filter((id) => id !== term_id)
				: [...prev.portfolios, term_id];
			return { ...prev, portfolios };
		});
	};

	const handleSearch = () => {
		setFiltersGlobal((prev) => ({
			...prev,
			search_term: searchTerm,
			discipline: filters.disciplines,
			media_type: filters.media,
			category: filters.cats,
			portfolio: filters.portfolios,
		}));
		setAdditionalGlobal((prev) => ({
			...prev,
			page: 1,
		}));
		setContentGlobal([]);
		setTriggerRefresh(true);
	};

	const isAnyFilterActive =
		filters.cats.length > 0 ||
		filters.disciplines.length > 0 ||
		filters.media.length > 0 ||
		searchTerm.length > 0 ||
		filters.portfolios.length > 0;

	if (!data.success) {
		return (
			<div className={`${className}`}>
				<LoadingAnimation type="news-and-events-filters" />
			</div>
		);
	}

	return (
		<div className={`${className}`}>
			<div className={`${className}__headline-wrap`}>
				<span className={`${className}__filter`}>
					<Filter color="#42276c" size={25} />
					<h5 className={`${className}__headline`}>Filters</h5>
				</span>
				{isAnyFilterActive && (
					<span
						className={`${className}__clear`}
						onClick={() => handleReset()}
					>
						<X color="var(--c-white)" size={15} />
						Clear
					</span>
				)}
			</div>
			<div className={`${className}__panel`}>
				<div className={`${className}__section`}>
					<div className={`${className}__input-wrap`}>
						<input
							className={`${className}__input`}
							type="text"
							placeholder="Keyword Search..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
						<Search
							className={`${className}__icon`}
							color="var(--c-teal)"
							size={25}
						/>
					</div>
				</div>

				<div
					className={`${className}__section ${className}__section--cats`}
				>
					<Select
						className={`${className}__select`}
						isClearable
						ref={cat_InputRef}
						isMulti
						name="categories"
						placeholder="Categories"
						options={filters.catList}
						onChange={handleInputChange('cats')}
						styles={customStyles}
					/>
				</div>

				<div
					className={`${className}__section ${className}__section--disciplines`}
				>
					<Select
						className={`${className}__select`}
						isClearable
						ref={disciplines_InputRef}
						isMulti
						name="disciplines"
						options={filters.disciplineList}
						placeholder="Disciplines"
						onChange={handleInputChange('disciplines')}
						styles={customStyles}
					/>
				</div>
				<div
					className={`${className}__section ${className}__section--media`}
				>
					<Select
						className={`${className}__select`}
						isClearable
						ref={mediaType_InputRef}
						isMulti
						name="media_types"
						placeholder="Media Types"
						options={filters.mediaList}
						onChange={handleInputChange('media')}
						styles={customStyles}
					/>
				</div>
				{filters.portfolioList.length > 0 && (
					<div
						className={`${className}__section ${className}__section--portfolios`}
					>
						<p className={`${className}__section-header`}>
							Portfolios
						</p>
						<ul className={`${className}__portfolios`}>
							{filters.portfolioList.map((portfolio) => (
								<li
									className={`${className}__portfolios__item${
										filters.portfolios.includes(
											portfolio.value
										)
											? ` ${className}__portfolios__item--active`
											: ''
									}`}
									key={portfolio.value}
									data-id={portfolio.value}
									onClick={() =>
										handlePortfolioChange(portfolio.value)
									}
								>
									{portfolio.label}
								</li>
							))}
						</ul>
					</div>
				)}
				<div className={`${className}__button-wrap`}>
					<button
						className={`${className}__button`}
						onClick={handleSearch}
					>
						Search
					</button>
				</div>
			</div>
		</div>
	);
};

export default NewsAndMediaFilters;
